import { forwardRef } from 'react';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import {
    baselineOffset,
    useFitFontSize,
    VALUE_INK_SHADOW,
    VALUE_LINE_BOX,
} from '../shared/formFieldEmphasis';
import { FAMILY_CARD_FONT_FAMILY } from './familyCardFont';
import { buildFamilyCardLetter } from './familyCardFormEngine';

export const FAMILY_CARD_PAGE_WIDTH = 595;
export const FAMILY_CARD_PAGE_HEIGHT = 842;

const BODY_FONT_PX = 12;
/**
 * Typed answers print larger and heavier than the letter's own text so they
 * stay legible on a photocopy; one that outgrows its column falls back toward
 * the body size rather than being clipped.
 */
const VALUE_FONT_PX = 14;
const FIELD_LINE_HEIGHT_PX = 22;
/**
 * Answers start a little in from the label and sit all but on the dots, the way
 * something written on ruled paper does. The label's own baseline is the dotted
 * line, so anything more than a hair of lift reads as the answer floating above
 * its line instead of resting on it — a descender crossing the rule is what
 * handwriting does anyway, and costs far less than that gap.
 */
const VALUE_INSET = 6;
const VALUE_LIFT = 7;

/**
 * The dotted line is an empty box with a bottom border, so its own baseline —
 * and therefore the label's — sits on the dots. Its border box is that tall,
 * which is where an answer's baseline is measured from.
 */
const DOT_LINE_PAD = 3;
const DOT_LINE_BOTTOM = DOT_LINE_PAD + 1;

/** …and it is nudged this far under the label's baseline, as ruled paper is. */
const DOT_LINE_DROP = 2;

/**
 * An answer is placed by its baseline — `VALUE_LIFT` above the dotted line —
 * the same way the Form-13 preview places its values, so the html2canvas export
 * can be corrected with the same measured numbers.
 */
const valueTop = (size) => (
    DOT_LINE_BOTTOM - VALUE_LIFT - baselineOffset(size, VALUE_LINE_BOX * size)
);
const BLOCK_GAP = 10;
const ROW_GAP = 8;
const FOOTER_PAD_TOP = 20;
const TITLE_BOTTOM_GAP_PX = 28;

const PAGE_STYLE = {
    width: FAMILY_CARD_PAGE_WIDTH,
    minHeight: FAMILY_CARD_PAGE_HEIGHT,
    background: '#fff',
    color: '#111',
    padding: '36px 42px 44px',
    boxSizing: 'border-box',
    fontFamily: FAMILY_CARD_FONT_FAMILY,
    fontSize: BODY_FONT_PX,
    lineHeight: 1.5,
    position: 'relative',
    overflow: 'visible',
    display: 'flex',
    flexDirection: 'column',
};

const PHOTO_BOX = {
    position: 'absolute',
    top: 40,
    right: 44,
    width: 112,
    height: 140,
    border: '2px dashed #666',
    borderRadius: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: 6,
    boxSizing: 'border-box',
    background: '#fff',
};

const FIELD_FONT = {
    fontSize: BODY_FONT_PX,
    fontFamily: 'inherit',
    lineHeight: `${FIELD_LINE_HEIGHT_PX}px`,
};

function FieldLine({ label, value, indent = 0, noMargin = false }) {
    const hasValue = Boolean(value);
    const [valueRef, valueSize] = useFitFontSize(VALUE_FONT_PX, BODY_FONT_PX, [value, indent]);
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 4,
                width: indent ? `calc(100% - ${indent}px)` : '100%',
                marginLeft: indent,
                marginBottom: noMargin ? 0 : ROW_GAP,
            }}
        >
            <span
                className="family-card-field-label"
                style={{ ...FIELD_FONT, flexShrink: 0, whiteSpace: 'nowrap', paddingBottom: 3 }}
            >
                {label}
            </span>
            {/*
              * The dotted line runs the whole width of the blank whether or not
              * it is filled, and the answer sits on top of it — the way it does
              * on Form-13 and on a hand-written application.
              */}
            <span
                data-field-line
                style={{
                    flex: 1,
                    minWidth: 32,
                    position: 'relative',
                    top: DOT_LINE_DROP,
                    paddingBottom: DOT_LINE_PAD,
                    borderBottom: '1px dotted #222',
                }}
            >
                {hasValue ? (
                    <span
                        ref={valueRef}
                        data-field-value
                        data-filled="true"
                        className="family-card-field-value"
                        style={{
                            fontFamily: 'inherit',
                            fontSize: valueSize,
                            lineHeight: VALUE_LINE_BOX,
                            position: 'absolute',
                            left: VALUE_INSET,
                            top: valueTop(valueSize),
                            maxWidth: `calc(100% - ${VALUE_INSET * 2}px)`,
                            fontWeight: 400,
                            textShadow: VALUE_INK_SHADOW,
                            color: '#111',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {value}
                    </span>
                ) : null}
            </span>
        </div>
    );
}

function FamilyCountLine({ sons, daughters }) {
    const sonsFilled = sons !== '......';
    const daughtersFilled = daughters !== '......';
    // The count keeps its dotted line whether or not it is filled; the digit
    // rides above the dots, like every other answer on the page.
    const cellStyle = {
        display: 'inline-block',
        position: 'relative',
        width: 30,
        paddingBottom: DOT_LINE_PAD,
        borderBottom: '1px dotted #222',
    };
    const digitStyle = {
        fontFamily: 'inherit',
        fontSize: VALUE_FONT_PX,
        lineHeight: VALUE_LINE_BOX,
        position: 'absolute',
        left: 0,
        right: 0,
        top: valueTop(VALUE_FONT_PX),
        fontWeight: 400,
        textShadow: VALUE_INK_SHADOW,
        color: '#111',
        textAlign: 'center',
    };

    return (
        <div
            style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'baseline',
                columnGap: 28,
                rowGap: 8,
                marginLeft: 12,
            }}
        >
            <span style={{ ...FIELD_FONT, whiteSpace: 'nowrap' }}>
                ছেলে সন্তান:{' '}
                <span data-field-line style={cellStyle}>
                    {sonsFilled ? (
                        <span
                            data-field-value
                            data-filled="true"
                            className="family-card-field-value"
                            style={digitStyle}
                        >
                            {sons}
                        </span>
                    ) : null}
                </span>{' '}
                জন
            </span>
            <span style={{ ...FIELD_FONT, whiteSpace: 'nowrap' }}>
                মেয়ে সন্তান:{' '}
                <span data-field-line style={cellStyle}>
                    {daughtersFilled ? (
                        <span
                            data-field-value
                            data-filled="true"
                            className="family-card-field-value"
                            style={digitStyle}
                        >
                            {daughters}
                        </span>
                    ) : null}
                </span>{' '}
                জন
            </span>
        </div>
    );
}

function NumberedBlock({ number, children }) {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                marginBottom: BLOCK_GAP,
            }}
        >
            <span
                style={{ flexShrink: 0, width: 24, lineHeight: `${FIELD_LINE_HEIGHT_PX}px`, fontSize: BODY_FONT_PX }}
            >
                {number}।
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
        </div>
    );
}

function PreviewFieldRow({ left, right }) {
    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                columnGap: 20,
                marginLeft: 12,
                marginBottom: ROW_GAP,
                alignItems: 'end',
            }}
        >
            <div style={{ minWidth: 0 }}>{left}</div>
            <div style={{ minWidth: 0 }}>{right}</div>
        </div>
    );
}

const SIGNATURE_LINE_WIDTH = 200;

function SignatureBlock({ label, side = 'left' }) {
    const alignEnd = side === 'right';
    return (
        <div
            style={{
                flex: '1 1 0',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: alignEnd ? 'flex-end' : 'flex-start',
            }}
        >
            <div
                aria-hidden
                style={{
                    width: SIGNATURE_LINE_WIDTH,
                    height: 0,
                    borderTop: '1px solid #111',
                    marginBottom: 5,
                }}
            />
            <div
                style={{
                    color: '#111',
                    width: SIGNATURE_LINE_WIDTH,
                    whiteSpace: 'normal',
                    fontSize: BODY_FONT_PX,
                    fontWeight: 400,
                    textAlign: 'center',
                    lineHeight: 1.35,
                }}
            >
                {label}
            </div>
        </div>
    );
}

function FamilyCardFormPreview({ form }, ref) {
    const { t } = useTranslation();
    const recipientLabel = t(`familyCardForm.recipients.${form.recipientType}`);
    const d = buildFamilyCardLetter(form, recipientLabel, t);
    const unionFilled = Boolean(form.unionName?.trim());

    return (
        <div ref={ref} data-family-card-page style={PAGE_STYLE}>
            <div style={{ flex: '1 1 auto', minHeight: 0 }}>
                <div style={PHOTO_BOX}>
                    <span style={{ fontSize: 10, color: '#666', lineHeight: 1.45, fontWeight: 400 }}>
                        {t('familyCardForm.preview.photoHint')}
                    </span>
                </div>

                <div
                    data-title-block
                    style={{
                        paddingRight: 120,
                        textAlign: 'center',
                        boxSizing: 'border-box',
                    }}
                >
                    <div
                        style={{
                            fontWeight: 400,
                            fontSize: 14,
                            lineHeight: '20px',
                            textDecoration: 'underline',
                            textUnderlineOffset: 4,
                        }}
                    >
                        {t('familyCardForm.preview.title')}
                    </div>
                    <div
                        aria-hidden
                        data-title-gap
                        style={{
                            display: 'block',
                            width: '100%',
                            height: TITLE_BOTTOM_GAP_PX,
                            minHeight: TITLE_BOTTOM_GAP_PX,
                            flexShrink: 0,
                        }}
                    />
                </div>

                <div style={{ fontSize: BODY_FONT_PX, marginBottom: 2 }}>{t('familyCardForm.preview.to')}</div>
                <div style={{ fontSize: BODY_FONT_PX, marginBottom: 2, fontWeight: 400 }}>{d.recipient}</div>
                <div
                    style={{
                        // A typed union name is an answer like any other, so it
                        // gets the same weight as the fields below.
                        fontSize: unionFilled ? VALUE_FONT_PX : BODY_FONT_PX,
                        marginBottom: 16,
                        fontWeight: unionFilled ? 400 : 400,
                        textShadow: unionFilled ? VALUE_INK_SHADOW : 'none',
                    }}
                >
                    {d.unionDisplay}
                </div>

                <div style={{ fontSize: BODY_FONT_PX, marginBottom: 16 }}>
                    <span style={{ fontSize: BODY_FONT_PX, fontWeight: 400 }}>
                        {t('familyCardForm.preview.subject')}{' '}
                    </span>
                    <span style={{ fontSize: BODY_FONT_PX, fontWeight: 400 }}>
                        {t('familyCardForm.preview.subjectText')}
                    </span>
                </div>

                <div style={{ fontSize: BODY_FONT_PX, marginBottom: 8 }}>{t('familyCardForm.preview.sir')}</div>

                <div style={{ fontSize: BODY_FONT_PX, marginBottom: 16, textAlign: 'justify', paddingRight: 8 }}>
                    {t('familyCardForm.preview.body')}
                </div>

                <div style={{ marginBottom: 8 }}>
                    <NumberedBlock number="১">
                        <FieldLine label={t('familyCardForm.preview.applicantLabel')} value={d.applicantName} />
                    </NumberedBlock>
                    <NumberedBlock number="২">
                        <FieldLine label={t('familyCardForm.preview.fatherLabel')} value={d.fatherName} />
                    </NumberedBlock>
                    <NumberedBlock number="৩">
                        <FieldLine label={t('familyCardForm.preview.motherLabel')} value={d.motherName} />
                    </NumberedBlock>
                    <NumberedBlock number="৪">
                        <FieldLine label={t('familyCardForm.preview.nidLabel')} value={d.nid} />
                    </NumberedBlock>
                    <NumberedBlock number="৫">
                        <FieldLine label={t('familyCardForm.preview.mobileLabel')} value={d.mobile} />
                    </NumberedBlock>
                    <NumberedBlock number="৬">
                        <div style={{ fontSize: BODY_FONT_PX, fontWeight: 700, marginBottom: ROW_GAP }}>
                            {t('familyCardForm.preview.addressTitle')}
                        </div>
                        <PreviewFieldRow
                            left={(
                                <FieldLine
                                    label={t('familyCardForm.preview.villageLabel')}
                                    value={d.village}
                                    noMargin
                                />
                            )}
                            right={(
                                <FieldLine
                                    label={t('familyCardForm.preview.postOfficeLabel')}
                                    value={d.postOffice}
                                    noMargin
                                />
                            )}
                        />
                        <PreviewFieldRow
                            left={(
                                <FieldLine
                                    label={t('familyCardForm.preview.wardLabel')}
                                    value={d.wardNo}
                                    noMargin
                                />
                            )}
                            right={(
                                <FieldLine
                                    label={t('familyCardForm.preview.upazilaLabel')}
                                    value={d.upazila}
                                    noMargin
                                />
                            )}
                        />
                        <FieldLine
                            label={t('familyCardForm.preview.districtLabel')}
                            value={d.district}
                            indent={12}
                        />
                    </NumberedBlock>
                    <NumberedBlock number="৭">
                        <div style={{ fontSize: BODY_FONT_PX, fontWeight: 700, marginBottom: ROW_GAP }}>
                            {t('familyCardForm.preview.familyTitle')}
                        </div>
                        <FamilyCountLine sons={d.sonsCount} daughters={d.daughtersCount} />
                    </NumberedBlock>
                </div>
            </div>

            <div
                style={{
                    flexShrink: 0,
                    marginTop: 'auto',
                    paddingTop: FOOTER_PAD_TOP,
                }}
            >
                <div
                    style={{
                        fontSize: BODY_FONT_PX,
                        marginBottom: 20,
                        textAlign: 'justify',
                        paddingRight: 8,
                        lineHeight: 1.55,
                    }}
                >
                    {t('familyCardForm.preview.closing')}
                </div>

                <div
                    aria-hidden
                    style={{
                        minHeight: 56,
                        marginBottom: 16,
                    }}
                />

                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 32,
                    }}
                >
                    <SignatureBlock side="left" label={t('familyCardForm.preview.recipientSignature')} />
                    <SignatureBlock side="right" label={t('familyCardForm.preview.applicantSignature')} />
                </div>
            </div>
        </div>
    );
}

export default forwardRef(FamilyCardFormPreview);
